import * as React from 'react';
import * as echarts from 'echarts/core';
// Import bar charts
import { BarChart } from 'echarts/charts';

// Import the title, tooltip, rectangular coordinate system, dataset and transform components
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
} from 'echarts/components';
// Features like Universal Transition and Label Layout
import { LabelLayout, UniversalTransition } from 'echarts/features';

// Import the Canvas renderer
import { SVGRenderer } from 'echarts/renderers';

import { format, parseISO } from 'date-fns';

import type {
    DatasetComponentOption,
    GridComponentOption,
    BarSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
} from 'echarts';

// Create an Option type with only the required components and charts via ComposeOption
type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | GridComponentOption
    | DatasetComponentOption
    | LegendComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
    BarChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface ConversionData {
    period: string;
    converted: number;
    non_converted: number;
    period_start: string;
}

interface CustomerConversionChartProps {
    data?: ConversionData[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

const CustomerConversionChart = (props: CustomerConversionChartProps) => {
    const { data, period } = props;
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            // Clear chart if no data
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({
                    series: [{
                        data: []
                    }]
                });
            }
            return;
        }
        
        // Initialize chart
        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            // Format data for chart - create categories and series data
            const categories: string[] = [];
            const convertedData: number[] = [];
            const nonConvertedData: number[] = [];
            const totalsByIndex: number[] = [];

            data.forEach(item => {
                let date: Date;
                try {
                    if (item.period_start) {
                        date = parseISO(item.period_start);
                        if (isNaN(date.getTime())) {
                            date = new Date(item.period_start);
                        }
                    } else {
                        date = new Date();
                    }
                } catch (e) {
                    console.warn('Error parsing date:', item.period_start, e);
                    date = new Date(item.period_start || new Date());
                }

                // Format category label based on period
                let categoryLabel = '';
                if (period === 'daily') {
                    categoryLabel = format(date, 'MMM dd');
                } else if (period === 'weekly') {
                    categoryLabel = format(date, 'MMM dd');
                } else if (period === 'monthly') {
                    categoryLabel = format(date, 'MMM yyyy');
                } else {
                    categoryLabel = format(date, 'QQQ yyyy');
                }

                categories.push(categoryLabel);
                const converted = Number(item.converted) || 0;
                const nonConverted = Number(item.non_converted) || 0;
                convertedData.push(converted);
                nonConvertedData.push(nonConverted);
                totalsByIndex.push(converted + nonConverted);
            });

            const formatBarLabel = (params: any) => {
                const total = totalsByIndex[params.dataIndex] || 0;
                const value = Number(params.value) || 0;
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return `${value.toLocaleString()}\n${percentage}%`;
            };

            // Configure the chart
            const option: ECOption = {
                legend: {
                    data: ['Temporary User ID', 'Name Updated'],
                    top: 0,
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow',
                    },
                    formatter: (params: any) => {
                        let result = '';
                        if (Array.isArray(params)) {
                            const dateParam = params[0];
                            const date = new Date(data[dateParam.dataIndex]?.period_start || '');
                            let dateStr = '';
                            if (period === 'daily') {
                                dateStr = format(date, 'MMM dd, yyyy');
                            } else if (period === 'weekly') {
                                dateStr = format(date, 'MMM dd, yyyy');
                            } else if (period === 'monthly') {
                                dateStr = format(date, 'MMMM yyyy');
                            } else {
                                dateStr = format(date, 'QQQ yyyy');
                            }
                            const total = totalsByIndex[dateParam.dataIndex] || 0;
                            result = `${dateStr}<br/>`;
                            params.forEach((param: any) => {
                                const value = Number(param.value) || 0;
                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                                result += `${param.marker} ${param.seriesName}: ${value.toLocaleString()} (${percentage}%)<br/>`;
                            });
                            const profileCompletionRate = total > 0
                                ? ((convertedData[dateParam.dataIndex] / total) * 100).toFixed(1)
                                : '0.0';
                            result += `Total Customers: ${total.toLocaleString()}<br/>`;
                            result += `Name Updated Rate: ${profileCompletionRate}%`;
                        }
                        return result;
                    },
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '8%',
                    top: '18%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: {
                        rotate: categories.length > 10 ? 45 : 0,
                    },
                },
                yAxis: {
                    type: 'value',
                    axisLabel: {
                        formatter: (value: number) => `${value}`,
                    },
                },
                series: [
                    {
                        name: 'Temporary User ID',
                        type: 'bar',
                        data: nonConvertedData,
                        barMaxWidth: 36,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                        itemStyle: {
                            color: '#ff7875',
                        },
                    },
                    {
                        name: 'Name Updated',
                        type: 'bar',
                        data: convertedData,
                        barMaxWidth: 36,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                        itemStyle: {
                            color: '#52c41a',
                        },
                    },
                ],
            };

            // Apply the config
            chartInstance.current.setOption(option);
        }

        // Handle resize
        const handleResize = () => {
            chartInstance.current?.resize();
        };

        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            if (chartInstance.current) {
                chartInstance.current.dispose();
                chartInstance.current = null;
            }
        };
    }, [data, period]);

    return <div ref={chartRef} style={{ width: '100%', height: 360 }} />;
};

export default CustomerConversionChart;
